import React from 'react';
import { Link } from 'react-router-dom';

// Full article content — written for SEO + actual reader value.
// Each article is plain JSX so we can format paragraphs, lists, callouts.
// Exported so individual article pages (src/pages/ArticlePage.js) can
// look up an article by slug.
export const ARTICLES = [
  {
    slug: 'where-did-my-etsy-money-go',
    title: 'Where Did My Etsy Money Go? Every Fee Explained',
    summary: 'You sold for $30 but only got $26. Here\'s where the missing $4 went, line by line — listing, transaction, processing, regulatory, and the Offsite Ads gotcha.',
    readTime: '6 min read',
    tag: 'Beginner',
    tagColor: '#047857',
    content: (
      <>
        <p>You make a sale for <strong>$30 + $5 shipping</strong>. The buyer pays you $35. A few days later, you check your Etsy payment account and the deposit is closer to <strong>$30.50</strong>. Where did the missing money go? Let's break it down piece by piece.</p>

        <h4>1. Listing fee — $0.20</h4>
        <p>Etsy charges 20 cents to publish each listing, and another 20 cents every time it renews (every 4 months automatically, or after each sale on a sold-out listing). This is small but constant — if you have 100 listings, that's $20 just to keep them visible for the year.</p>

        <h4>2. Transaction fee — 6.5% of the total</h4>
        <p>This is the big one. Etsy charges 6.5% on the <strong>total order, including shipping</strong>. That means on your $35 order, the transaction fee is $2.28 — not $1.95. A lot of new sellers calculate this against the item price only, then get surprised when their numbers don't match.</p>

        <h4>3. Payment processing fee — 3% + $0.25 (US)</h4>
        <p>Every time someone pays via Etsy Payments, there's a card-processing cut. In the US it's 3% + $0.25. On a $35 order that's $1.30. Outside the US, the rate varies — UK sellers pay 4% + £0.20, German sellers pay 4% + €0.30, and so on.</p>

        <h4>4. Regulatory operating fee — 0.3-1.1% (some countries)</h4>
        <p>Sellers in the UK, France, Italy, Spain, and a few other countries pay an additional regulatory fee tied to local digital-services taxes. It's small per sale but it adds up. Etsy is changing several of these rates on June 22, 2026 — the calculator's Updates tab tracks all known changes.</p>

        <h4>5. The Offsite Ads gotcha — 12-15%</h4>
        <p>Here's where sellers get blindsided. If your sale comes from an offsite ad (Google, Facebook, Instagram, Pinterest, etc.), Etsy charges an additional <strong>12-15% on the entire order</strong>. On our $35 example, that's another $4.20-5.25. Suddenly your $30 item nets you closer to $24.</p>

        <p style={{ background: 'rgba(180, 83, 9, 0.08)', border: '1px solid rgba(180, 83, 9, 0.3)', borderRadius: '0.5rem', padding: '0.85rem 1rem', marginTop: '1.2rem' }}>
          <strong>The takeaway:</strong> Etsy effectively keeps 10-15% of every sale, or 22-30% if Offsite Ads triggers. That's before you've paid for materials, packaging, or your own time. Use the calculator's Snapshot tab — the headline "Etsy keeps X%" tells you the answer for your specific product in real time.
        </p>
      </>
    ),
  },
  {
    slug: 'etsy-vs-shopify-vs-amazon-handmade',
    title: 'Etsy vs Shopify vs Amazon Handmade — Which Wins For You?',
    summary: 'Etsy has built-in traffic. Shopify has lower per-sale fees. Amazon has 250M shoppers. The right answer depends entirely on your monthly volume — here\'s the math.',
    readTime: '8 min read',
    tag: 'Strategy',
    tagColor: '#1e3a8a',
    content: (
      <>
        <p>The three platforms look similar from the outside — sell handmade goods online, get paid. But the fee structures are wildly different, and the best one depends almost entirely on <strong>your monthly volume</strong>.</p>

        <h4>Etsy — best at low-medium volume</h4>
        <p>No monthly fee. You pay only when you sell. Effective per-sale cut is roughly <strong>10-15%</strong> (or 22-30% if Offsite Ads triggers). Built-in marketplace audience means buyers are searching specifically for handmade items. Downside: you compete with millions of other sellers and have minimal brand control.</p>

        <h4>Shopify Basic — best at high volume</h4>
        <p>$39/month subscription, plus 2.9% + 30¢ per transaction via Shopify Payments. That's an effective cut of roughly <strong>3-5% per sale</strong>, which is dramatically lower than Etsy — but you absorb $39/month whether you sell or not. Plus you have to drive your own traffic (ads, SEO, social, email). No built-in audience.</p>

        <h4>Amazon Handmade — best for products that benefit from Prime</h4>
        <p>15% referral fee on every sale. The Pro selling plan is $39.99/month but it's <strong>waived for Handmade sellers</strong>. Amazon brings 250 million+ Prime shoppers, but the application process is selective and competition for buy-box visibility is fierce.</p>

        <h4>The crossover math</h4>
        <p>For most products, the breakeven looks like this:</p>
        <ul>
          <li><strong>0-30 sales/month</strong>: Etsy wins. You can't justify Shopify's $39/month overhead.</li>
          <li><strong>30-80 sales/month</strong>: Often a tie. Run the numbers in the Platforms tab — your specific price/cost ratio matters.</li>
          <li><strong>80+ sales/month</strong>: Shopify usually wins. The $39/month is amortized over enough sales that the per-sale fee advantage dominates.</li>
          <li><strong>Anywhere with Amazon-friendly product</strong>: Amazon Handmade can be a strong second channel.</li>
        </ul>

        <p style={{ background: 'rgba(30, 58, 138, 0.08)', border: '1px solid rgba(30, 58, 138, 0.3)', borderRadius: '0.5rem', padding: '0.85rem 1rem', marginTop: '1.2rem' }}>
          <strong>Best practice:</strong> start on Etsy to validate demand and build initial reviews, then add Shopify as a second channel once you're consistently doing 30+ sales/month. Don't choose one or the other — most successful handmade businesses sell on multiple platforms.
        </p>
      </>
    ),
  },
  {
    slug: 'how-to-price-handmade-goods',
    title: 'How to Price Handmade Goods on Etsy (Without Losing Money)',
    summary: 'The most common pricing mistake: setting prices based on what feels right. Here\'s a structured approach using cost-plus pricing with Etsy fees built in.',
    readTime: '7 min read',
    tag: 'Pricing',
    tagColor: '#b45309',
    content: (
      <>
        <p>The single biggest pricing mistake new Etsy sellers make: setting prices based on <em>what feels right</em>. They look at competitors, eyeball a number, and hope it works out. Then they discover six months later they've been losing money on every sale.</p>

        <h4>The cost-plus formula</h4>
        <p>A reliable starting point:</p>
        <p style={{ fontFamily: 'monospace', background: 'rgba(26,26,46,0.05)', padding: '0.75rem 1rem', borderRadius: '0.4rem', fontSize: '0.92rem' }}>
          Price = (Materials + Labor + Overhead) × Markup ÷ (1 − Etsy fee rate)
        </p>

        <p>Each variable matters:</p>
        <ul>
          <li><strong>Materials</strong>: every physical thing that goes into one unit. Beads, leather, ink, paper, packaging, shipping label, thank-you card.</li>
          <li><strong>Labor</strong>: hours × your hourly rate. Most sellers undercount this — track honestly for a week before guessing.</li>
          <li><strong>Overhead</strong>: studio rent, tools, software, listing fees averaged per unit. Small but real.</li>
          <li><strong>Markup</strong>: 2x is the floor. 2.5-3x is healthy. Below 2x leaves no room for sales/discounts.</li>
          <li><strong>Etsy fee rate</strong>: ~12-15% before Offsite Ads. The denominator (1 − fee rate) ensures your markup actually lands after fees.</li>
        </ul>

        <h4>Worked example</h4>
        <p>Hand-stitched leather wallet. Materials: $8. Labor: 1.5 hrs × $20 = $30. Overhead: $1. Total cost: $39. Markup 2.5x = $97.50. Divide by (1 − 0.13) = $112. <strong>Round to $115.</strong></p>

        <p>The price feels high, but at $115 you'll net roughly $58 after fees and costs — a 50% margin. At $80 (a price that "feels right"), you'd net roughly $30 — a 37% margin. The difference is real money.</p>

        <h4>Common pricing pitfalls</h4>
        <ul>
          <li><strong>Forgetting the transaction fee applies to shipping.</strong> If you charge $7 shipping, Etsy takes 6.5% of that too.</li>
          <li><strong>Pricing for friends.</strong> Friends and family will tell you your prices are "totally fair." Strangers vote with their wallets.</li>
          <li><strong>Not raising prices over time.</strong> Material costs go up. Your skills go up. Your prices should too.</li>
          <li><strong>Underselling labor.</strong> $10/hour is the minimum wage in most US states. If you're charging $5/hour effective for handmade work, you're below McDonald's.</li>
        </ul>

        <p style={{ background: 'rgba(4, 120, 87, 0.08)', border: '1px solid rgba(4, 120, 87, 0.3)', borderRadius: '0.5rem', padding: '0.85rem 1rem', marginTop: '1.2rem' }}>
          <strong>Use the Find Price tab</strong> in this calculator to reverse the math — tell it the profit you want, it gives you the price.
        </p>
      </>
    ),
  },
  {
    slug: 'etsy-fees-by-country-2026',
    title: 'Etsy Fees by Country — A 2026 Guide to Regional Differences',
    summary: 'Listing fees and transaction fees are universal, but processing fees and regulatory operating fees vary by country. Here\'s what UK, EU, Canada, and Australia sellers pay.',
    readTime: '9 min read',
    tag: 'International',
    tagColor: '#7e22ce',
    content: (
      <>
        <p>Most fee guides on the web assume you're a US seller. If you're in the UK, EU, Canada, Australia, or anywhere else — your numbers are different, sometimes meaningfully.</p>

        <h4>What's universal</h4>
        <ul>
          <li><strong>Listing fee:</strong> $0.20 USD (charged in your local currency at conversion)</li>
          <li><strong>Transaction fee:</strong> 6.5% of total order including shipping</li>
          <li><strong>Offsite Ads (when triggered):</strong> 12% if forced in, 15% if opted in</li>
        </ul>

        <h4>What varies — payment processing</h4>
        <p>Etsy Payments processing fees differ significantly by country:</p>
        <ul>
          <li><strong>United States:</strong> 3% + $0.25</li>
          <li><strong>United Kingdom:</strong> 4% + £0.20</li>
          <li><strong>Germany / Austria / France / Spain / Italy:</strong> 4% + €0.30</li>
          <li><strong>Canada:</strong> 3% + CA$0.25</li>
          <li><strong>Australia:</strong> 3% + AU$0.25</li>
          <li><strong>Netherlands / Belgium / Ireland:</strong> 4% + €0.30</li>
        </ul>
        <p>The UK and EU rates are roughly 1 percentage point higher than the US — over a year that's real money.</p>

        <h4>Regulatory operating fees</h4>
        <p>Several countries levy additional fees tied to local digital services taxes. These rates change periodically — Etsy announced significant updates effective June 22, 2026.</p>
        <ul>
          <li><strong>UK:</strong> ~0.25% currently, rising to ~1.10% on Jun 22, 2026</li>
          <li><strong>France:</strong> ~0.25% currently, rising to ~1.10%</li>
          <li><strong>Italy:</strong> ~0.25% currently, rising to ~1.10%</li>
          <li><strong>Spain:</strong> ~0.40% currently, rising to ~1.10%</li>
          <li><strong>Turkey:</strong> ~1.10% currently, rising to ~3.00%</li>
        </ul>

        <h4>VAT / GST considerations</h4>
        <p>Etsy collects and remits VAT on behalf of sellers in many EU countries, the UK, Australia, New Zealand, and others. This <strong>doesn't reduce your earnings</strong> — Etsy adds VAT to the buyer's price — but it can confuse new sellers who see VAT amounts in their order summaries.</p>

        <p style={{ background: 'rgba(126, 34, 206, 0.08)', border: '1px solid rgba(126, 34, 206, 0.3)', borderRadius: '0.5rem', padding: '0.85rem 1rem', marginTop: '1.2rem' }}>
          <strong>The calculator handles all 61 countries</strong> with verified rates as of April 2026, plus a separate tracker for Jun 22 changes. Pick your country in the inputs panel and the math adjusts automatically.
        </p>
      </>
    ),
  },
  {
    slug: 'should-i-use-free-shipping',
    title: 'Should I Offer Free Shipping on Etsy?',
    summary: 'Etsy\'s algorithm boosts free-shipping listings in search. But the math gets confusing fast. Here\'s when to absorb shipping into the item price and when to keep it separate.',
    readTime: '5 min read',
    tag: 'Pricing',
    tagColor: '#b45309',
    content: (
      <>
        <p>"Free shipping" on Etsy is mostly marketing — you're not actually giving shipping away, you're baking it into the item price. The question is whether it's worth doing.</p>

        <h4>What free shipping actually does</h4>
        <p>Etsy launched the free-shipping algorithm boost in 2019. Listings with free shipping (or where the seller offers free shipping over $35) get preferential placement in search results — meaningfully so for US buyers searching from US shops.</p>

        <h4>The fee math is unchanged</h4>
        <p>Common misconception: <em>"if I bake shipping into the price, I'll pay more in transaction fees."</em> Actually, no — Etsy's 6.5% transaction fee already applies to the full order, <strong>shipping included</strong>. So whether you charge $30 + $5 shipping or $35 + $0 shipping, the transaction fee is identical: 6.5% of $35 = $2.28.</p>

        <h4>When free shipping wins</h4>
        <ul>
          <li>You sell to US buyers (the algorithm boost is most pronounced there)</li>
          <li>Your shipping costs are predictable (under $7-8 typically)</li>
          <li>You're competing in a crowded category where search rank matters a lot</li>
          <li>Your items are individually priced over $35 (Etsy's free-shipping threshold)</li>
        </ul>

        <h4>When free shipping loses</h4>
        <ul>
          <li>You ship internationally — international postage is too unpredictable to absorb safely</li>
          <li>Your items are heavy, fragile, or oversized (shipping costs vary too widely by destination)</li>
          <li>You sell low-priced items where adding $5-7 to the sticker price meaningfully hurts conversion</li>
        </ul>

        <p style={{ background: 'rgba(180, 83, 9, 0.08)', border: '1px solid rgba(180, 83, 9, 0.3)', borderRadius: '0.5rem', padding: '0.85rem 1rem', marginTop: '1.2rem' }}>
          <strong>Practical approach:</strong> for US-focused shops with consistent shipping costs, build a "free shipping" version of each listing with shipping costs baked in. For international or hard-to-ship items, keep separate shipping. Many top sellers run both, A/B testing per product.
        </p>
      </>
    ),
  },
  {
    slug: 'when-to-opt-out-offsite-ads',
    title: 'When to Opt Out of Etsy Offsite Ads (and When You Can\'t)',
    summary: 'Sellers under $10K/yr can opt out. Sellers above can\'t. Here\'s how to think about whether the 12-15% fee is worth it for your shop.',
    readTime: '4 min read',
    tag: 'Strategy',
    tagColor: '#1e3a8a',
    content: (
      <>
        <p>Offsite Ads is Etsy's program where they advertise your listings on Google, Facebook, Instagram, Pinterest, Bing, and other external platforms. When a buyer clicks the ad and buys within 30 days, Etsy charges you a fee.</p>

        <h4>The two rates</h4>
        <ul>
          <li><strong>15%</strong> if your shop has earned under $10,000 in the trailing 12 months <em>and</em> you opt in</li>
          <li><strong>12%</strong> if your shop has earned over $10,000 in the trailing 12 months (mandatory — you cannot opt out)</li>
        </ul>

        <h4>Who can opt out</h4>
        <p>Only sellers under $10K/yr in trailing revenue. If you're under that threshold, the program is opt-in by default — you have to actively turn it on.</p>

        <h4>The math you need to run</h4>
        <p>Imagine a $40 sale comes through Offsite Ads at 15%. That's $6 off the top, on a sale you would not have made without the ad. Net result depends on:</p>
        <ul>
          <li><strong>Your margin before Offsite Ads:</strong> if you're at 40%+ net margin, you can afford the cut</li>
          <li><strong>Whether the ad triggered an actual incremental sale</strong>: this is impossible to know with certainty, but Etsy's data suggests Offsite-attributed sales are typically additive</li>
          <li><strong>Repeat buyer behavior:</strong> someone who finds you via Offsite may become a direct customer for future purchases</li>
        </ul>

        <h4>Common reasons to opt out</h4>
        <ul>
          <li>Your margins are already thin (sub-25%) — losing 15% can flip you to a loss</li>
          <li>You sell low-priced items where the math doesn't work</li>
          <li>You're confident in your direct-traffic and Etsy-internal-search performance</li>
        </ul>

        <h4>Common reasons to opt in (when eligible)</h4>
        <ul>
          <li>You have healthy 35%+ margins and can absorb the fee</li>
          <li>Your category has strong external search demand (jewelry, prints, home decor)</li>
          <li>You're trying to grow fast and need volume more than per-sale margin</li>
        </ul>

        <p style={{ background: 'rgba(30, 58, 138, 0.08)', border: '1px solid rgba(30, 58, 138, 0.3)', borderRadius: '0.5rem', padding: '0.85rem 1rem', marginTop: '1.2rem' }}>
          <strong>Use the calculator's Offsite toggle</strong> to compare the same product with and without the 15% fee. The Insights tab will flag whether Offsite is significantly affecting your bottom line.
        </p>
      </>
    ),
  },
];

export default function ArticlesSection() {
  return (
    <section
      id="articles"
      style={{
        padding: '3rem 1rem 2rem',
        background: '#fefdf8',
      }}
    >
      <div style={{ maxWidth: '64rem', margin: '0 auto' }}>
        <h2
          style={{
            fontFamily: '"Playfair Display", serif',
            fontSize: '2.2rem',
            fontWeight: 700,
            color: '#1a1a2e',
            marginBottom: '0.5rem',
            letterSpacing: '-0.01em',
          }}
        >
          📖 Guides &amp; articles
        </h2>
        <p
          style={{
            fontFamily: 'Inter, sans-serif',
            fontSize: '1.02rem',
            color: 'rgba(26, 26, 46, 0.82)',
            marginBottom: '2rem',
            lineHeight: 1.55,
          }}
        >
          Practical guides for sellers who want to actually keep what they earn.
        </p>

        {/* Card grid — each card is now a real route link to /articles/:slug */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
            gap: '1rem',
          }}
        >
          {ARTICLES.map(article => (
            <Link
              key={article.slug}
              to={`/articles/${article.slug}`}
              style={{
                background: '#ffffff',
                border: '1.5px solid rgba(26, 26, 46, 0.12)',
                borderRadius: '0.75rem',
                padding: '1.1rem',
                textAlign: 'left',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                gap: '0.5rem',
                transition: 'all 0.15s',
                boxShadow: '0 1px 2px rgba(26, 26, 46, 0.04)',
                fontFamily: 'inherit',
                color: 'inherit',
                textDecoration: 'none',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#1e3a8a';
                e.currentTarget.style.boxShadow = '0 2px 4px rgba(26, 26, 46, 0.08), 0 8px 16px rgba(30, 58, 138, 0.08)';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'rgba(26, 26, 46, 0.12)';
                e.currentTarget.style.boxShadow = '0 1px 2px rgba(26, 26, 46, 0.04)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span
                  style={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    color: article.tagColor,
                    background: `${article.tagColor}18`,
                    padding: '0.18rem 0.5rem',
                    borderRadius: '999px',
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                  }}
                >
                  {article.tag}
                </span>
                <span
                  style={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '0.78rem',
                    color: 'rgba(26, 26, 46, 0.6)',
                  }}
                >
                  {article.readTime}
                </span>
              </div>
              <h3
                style={{
                  fontFamily: '"Playfair Display", serif',
                  fontSize: '1.2rem',
                  fontWeight: 600,
                  color: '#1a1a2e',
                  lineHeight: 1.3,
                  margin: 0,
                }}
              >
                {article.title}
              </h3>
              <p
                style={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '0.93rem',
                  color: 'rgba(26, 26, 46, 0.78)',
                  lineHeight: 1.55,
                  margin: 0,
                }}
              >
                {article.summary}
              </p>
              <span
                style={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '0.86rem',
                  fontWeight: 600,
                  color: '#1e3a8a',
                  marginTop: '0.25rem',
                }}
              >
                Read more →
              </span>
            </Link>
          ))}
        </div>

        <p
          style={{
            fontFamily: 'Inter, sans-serif',
            fontSize: '0.86rem',
            color: 'rgba(26, 26, 46, 0.62)',
            marginTop: '1.5rem',
            textAlign: 'center',
          }}
        >
          ~ click any article to read · more guides published monthly ~
        </p>
      </div>
    </section>
  );
}
